import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@r/components/ui/card.tsx';
import { Button } from '@r/components/ui/button.tsx';
import { Plus, TrendingUp } from 'lucide-react';
import { CreateBoosterDialog } from '@admin/components/boosters/create-booster-dialog.tsx';
import { SelectBoosterReferencesDialog } from '@admin/components/boosters/select-booster-references-dialog.tsx';
import { BoosterList } from '@admin/components/boosters/booster-list.tsx';
import type { BoosterDraftData } from '@admin/components/boosters/create-booster-form-schema.ts';
import {
  EMPTY_BOOSTER_REFERENCES_SELECTION,
  type BoosterReferencesSelectionState
} from '@admin/components/boosters/booster-references-selection.ts';

/** Page admin de gestion des boosters (lancement, liste et flux de création). */
export function Boosters() {
  const { t } = useTranslation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isReferencesDialogOpen, setIsReferencesDialogOpen] = useState(false);
  const [boosterDraft, setBoosterDraft] = useState<BoosterDraftData | null>(null);
  const [referencesSelection, setReferencesSelection] = useState<BoosterReferencesSelectionState>(
    EMPTY_BOOSTER_REFERENCES_SELECTION
  );

  // Passe du formulaire initial à la modale de sélection des références.
  const handleCreateNext = (draft: BoosterDraftData) => {
    setBoosterDraft(draft);
    setIsReferencesDialogOpen(true);
  };

  // Retour à l'étape 1 en conservant le brouillon en mémoire.
  const handleReferencesBack = () => {
    setIsReferencesDialogOpen(false);
    setIsCreateDialogOpen(true);
  };

  // Le brouillon reste en mémoire tant que la création n'est pas confirmée.
  const handleCreateSuccess = () => {
    setBoosterDraft(null);
    setReferencesSelection(EMPTY_BOOSTER_REFERENCES_SELECTION);
  };

  return (
    <div className="space-y-10">
      <h1 className="text-4xl font-bold">{t('menu.admin.animations.boosters')}</h1>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h2 className="flex gap-2 items-center">
              <TrendingUp className="w-6 h-6" />
              <p className="text-xl font-bold">{t('boosters.cta.title')}</p>
            </h2>
            <p className="text-muted-foreground">{t('boosters.cta.description')}</p>
          </div>

          <div className="flex mt-6">
            <Button rounded="full" type="button" onClick={() => setIsCreateDialogOpen(true)}>
              {t('boosters.cta.button')} <Plus />
            </Button>
          </div>
        </CardContent>
      </Card>

      <BoosterList />

      <CreateBoosterDialog
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onNext={handleCreateNext}
        defaultValues={boosterDraft}
      />

      <SelectBoosterReferencesDialog
        isOpen={isReferencesDialogOpen}
        onOpenChange={setIsReferencesDialogOpen}
        draft={boosterDraft}
        referencesSelection={referencesSelection}
        onReferencesSelectionChange={setReferencesSelection}
        onBack={handleReferencesBack}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
}
